import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { 
  BarChart3,
  Globe, 
  Keyboard, 
  Settings, 
  Eye, 
  Layers, 
  AlertTriangle,
  Mail,
  Bell,
  LogOut,
  Edit,
  ChevronRight,
  Code,
  Shield,
  Activity,
  Users,
  Lock,
  Key,
  CreditCard,
  History
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/use-auth";
import { Logo } from "@/components/logo";

const navigationItems = [
  {
    title: "Site Data Overview",
    href: "/",
    icon: BarChart3,
    badge: <Edit className="w-3 h-3" />,
  },
  {
    title: "Website Domains", 
    href: "/domains",
    icon: Globe,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "User Inputs",
    href: "/user-inputs",
    icon: Keyboard,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Request Parameters",
    href: "/request-parameters", 
    icon: Settings,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Third-party Trackers",
    href: "/trackers",
    icon: Eye,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Applications",
    href: "/applications",
    icon: Layers,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Scheduled Scans",
    href: "/scheduled-scans",
    icon: Bell,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "JavaScript Inventory",
    href: "/scripts",
    icon: Code,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Change History",
    href: "/change-history",
    icon: History,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "CSP Header Analysis",
    href: "/csp",
    icon: Shield,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Weak TLS Ciphers",
    href: "/weak-tls-ciphers",
    icon: Lock,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Third-party Domain Analysis",
    href: "/network-analysis",
    icon: Globe,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Real-Time Compliance",
    href: "/compliance",
    icon: Activity,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Privacy Validation",
    href: "/privacy-validation",
    icon: Shield,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Global Threat Intelligence",
    href: "/threat-intelligence",
    icon: Activity,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Sensitive Actions",
    href: "/sensitive-actions",
    icon: AlertTriangle,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Account Settings",
    href: "/settings",
    icon: Settings,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Multi-Factor Auth",
    href: "/mfa-setup",
    icon: Key,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "User Management",
    href: "/user-management",
    icon: Users,
    badge: <ChevronRight className="w-3 h-3" />,
  },
  {
    title: "Subscription & Plans",
    href: "/subscription",
    icon: CreditCard,
    badge: <ChevronRight className="w-3 h-3" />,
  },
];

const quickActions = [
  {
    title: "Send Report",
    icon: Mail,
    href: "/sendreport",
  },
];

export function Sidebar() {
  const [location] = useLocation();
  const { signOut, user, isAuthenticated } = useAuth();

  // Always render sidebar for authenticated users, with fallback for loading states
  if (!isAuthenticated) {
    console.log("Sidebar: User not authenticated, not rendering");
    return null;
  }
  
  return (
    <nav className="h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col" data-testid="main-sidebar">
      {/* PCI Scan Report Logo/Header */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <Logo size="md" variant="default" />
      </div>
      
      <div className="p-4 space-y-2">
        {/* Overview Section */}
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const isActive = location === item.href;
            
            return (
              <div key={item.href} className="relative">
                <Link 
                  href={item.href}
                  data-testid={`nav-link-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "block w-full text-decoration-none hover:no-underline",
                    isActive ? "text-emerald-600" : "text-slate-700 dark:text-slate-300"
                  )}
                  onClick={() => {}}
                >
                  <div className={cn(
                    "flex items-center w-full space-x-3 px-3 py-2 rounded-md transition-colors cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/30",
                    isActive 
                      ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400" 
                      : "text-slate-700 dark:text-slate-300 hover:text-emerald-600"
                  )}>
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left font-medium text-sm">{item.title}</span>
                    <div className="flex-shrink-0 text-xs opacity-60">
                      {item.badge}
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="pt-4 space-y-1">
          {quickActions.map((action) => (
            <Link 
              key={action.href} 
              href={action.href}
              data-testid={`nav-link-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="flex items-center w-full space-x-3 px-3 py-2 rounded-md transition-colors cursor-pointer text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-emerald-600 no-underline"
              onClick={(e) => {
                console.log("Quick action clicked:", action.href, action.title);
              }}
              style={{ pointerEvents: 'auto', zIndex: 10, textDecoration: 'none', display: 'flex' }}
            >
              <action.icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 text-left text-sm">{action.title}</span>
            </Link>
          ))}
        </div>
      </div>

      {/* Sign Out Button at the bottom */}
      <div className="mt-auto p-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          onClick={() => signOut()}
          variant="destructive"
          className="w-full flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white border-red-600"
          data-testid="button-signout"
        >
          <LogOut className="h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </nav>
  );
}
